import { apiv2_getVizToken } from "@/app/api2"
import { useGlobalData } from "@/app/global_data"
import { Button } from "@/components/buttons/button"
import { Spinner } from "@/components/layouts/spinner"
import { useTableauParameters } from "@/components/tableau/hooks"
import { useTableauVizInteractive } from "@/components/tableau/hooks/use_tableau_interactive"
import { TableauVizControls } from "@/components/tableau/ui/tableau_viz_controls"
import { useQuery } from "@tanstack/react-query"
import Script from "next/script"
import { useEffect, useRef, useState } from "react"

type TableauVizProps = {
  src: string
  slotKey: string | null
  // pulse parameters are received through CMS API along with the src url
  pulseParameters?: {
    name: string
    value: string
  }[]
  isMonitor?: boolean
  tableauParameters?: {
    width: string
    height: string
  }
}

export function TableauViz({
  src,
  slotKey,
  isMonitor = false,
  pulseParameters,
  tableauParameters,
}: TableauVizProps) {
  const vizRef = useRef<any | null>(null)
  const [isVizReady, setIsVizReady] = useState(false)

  const globalData = useGlobalData()
  const { isLoaded } = globalData.get()

  const {
    data: token,
    isLoading: isTokenLoading,
    error: tokenError,
    refetch,
  } = useQuery({
    queryKey: ["token"],
    queryFn: apiv2_getVizToken,
    enabled: isMonitor ? isLoaded : true,
  })

  const { getWorkbookParameters, changeParameterValue } =
    useTableauParameters(vizRef)

  const interactiveData = useTableauVizInteractive({
    slotKey,
    changeParameterValue,
  })

  useEffect(() => {
    if (!token || !vizRef.current || !isLoaded) return

    function handleFirstInteractive() {
      setIsVizReady(true)
      getWorkbookParameters()
    }

    const viz = vizRef.current
    viz.addEventListener("firstinteractive", handleFirstInteractive)

    return () => {
      viz?.removeEventListener("firstinteractive", handleFirstInteractive)
    }
  }, [token, isLoaded, getWorkbookParameters])

  // effect dedicated to Pulse Tableau Viz
  useEffect(() => {
    if (!isVizReady) return

    async function updateParameters() {
      try {
        if (!pulseParameters) throw new Error("No pulse parameters found")

        const params: { name: string; value: string }[] = [
          ...pulseParameters,
          { name: "UniverseScreen", value: "Peer Group" },
        ]

        params.map(
          async ({ name, value }) => await changeParameterValue(name, value)
        )
      } catch (error) {
        console.error("Failed to update parameters:", error)
      }
    }

    updateParameters()
  }, [pulseParameters])

  if (tokenError) {
    return (
      <div className="flex w-full items-center justify-center p-8">
        <div className="text-red-600">Failed to load visualization token</div>
        <Button color="tertiary" onClick={refetch}>
          Refresh
        </Button>
      </div>
    )
  }

  if (!token || isTokenLoading || !isLoaded) {
    return <Spinner hasTitle title="Loading Visualization..." />
  }

  return (
    <div className="flex w-full flex-col">
      <Script
        type="module"
        src="https://10ay.online.tableau.com/javascripts/api/tableau.embedding.3.latest.min.js"
      />

      <div className="w-full">
        {isVizReady && (
          <TableauVizControls
            changeParameterValue={changeParameterValue}
            slotKey={slotKey}
          />
        )}

        <tableau-viz
          id="tableau-viz"
          src={src}
          toolbar="hidden"
          hide-tabs
          token={token.jwt}
          width={tableauParameters ? tableauParameters.width : "1200"}
          height={tableauParameters ? tableauParameters.height : "950"}
          className="h-full w-full"
          ref={vizRef}>
          {interactiveData &&
            interactiveData.isEntityEnabled &&
            interactiveData.entityRefId && (
              <viz-parameter
                name="p.ref_firm_id"
                value={interactiveData.entityRefId}
              />
            )}
          {interactiveData &&
            interactiveData.isComparisonEnabled &&
            interactiveData.comparisonRefIds && (
              <viz-parameter
                name="p.member_firm_id"
                value={interactiveData.comparisonRefIds}
              />
            )}
        </tableau-viz>
      </div>
    </div>
  )
}
